import React from 'react';
import { ArrowLeft, Trash2, AlertTriangle, CheckCircle, XCircle } from 'lucide-react';
import DoodleBackground from '../components/DoodleBackground';

const DeleteAccount = () => {
  return (
    <div>
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        background: 'rgba(255,255,255,.97)', boxShadow: '0 2px 20px rgba(120,81,169,.08)',
        backdropFilter: 'blur(16px)', padding: '10px 0',
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <a href="/" style={{ display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none', color: 'inherit' }}>
            <img src="/logo.png" alt="Spee'V'Dee" style={{ width: 44, height: 44, borderRadius: '50%' }} />
            <span className="logo-text"><span className="bk">Spee</span><span className="vv">'V'</span><span className="bk">Dee</span></span>
          </a>
          <a href="/" className="btn btn-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#7851A9', fontWeight: 700, fontSize: 14, textDecoration: 'none', padding: '8px 20px', borderRadius: '50px', border: '2px solid #7851A9' }}>
            <ArrowLeft size={16} /> Back to Home
          </a>
        </div>
      </nav>

      <section style={{ background: '#f9fafb', position: 'relative', overflow: 'hidden', paddingTop: 100, paddingBottom: 80 }}>
        <DoodleBackground />
        <div style={{ maxWidth: 800, margin: '0 auto', padding: '0 24px', position: 'relative', zIndex: 1 }}>
          <div className="section-header">
            <span className="section-tag" style={{ background: 'rgba(220,38,38,.1)', color: '#DC2626' }}>ACCOUNT</span>
            <h2>Delete Your Account</h2>
            <p style={{ color: '#6B7280', fontSize: 14 }}>We're sorry to see you go. Here's how to delete your Spee'V'Dee account and what happens when you do.</p>
          </div>

          {/* Pre-Deletion Checklist */}
          <div style={{ background: '#FEF3C7', borderRadius: 16, padding: '24px 28px', marginBottom: 24, border: '1px solid #FCD34D' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <AlertTriangle size={20} color="#D97706" />
              <h3 style={{ margin: 0, fontSize: 16, color: '#92400E' }}>Before You Delete</h3>
            </div>
            <ul style={{ paddingLeft: 20, margin: 0, fontSize: 14, lineHeight: 2, color: '#78350F' }}>
              <li>Complete or cancel all active deliveries</li>
              <li>Withdraw any remaining earnings (Couriers)</li>
              <li>Download any receipts or delivery records you need</li>
              <li>Cancel your subscription to avoid future charges</li>
              <li>Account deletion is <strong>permanent and cannot be undone</strong></li>
            </ul>
          </div>

          {/* Steps */}
          <div style={{ background: '#fff', borderRadius: 16, padding: '32px 28px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', marginBottom: 24 }}>
            <h3 style={{ color: '#7851A9', marginTop: 0, fontSize: 18 }}>How to Delete Your Account</h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 20, marginTop: 20 }}>
              {[
                { step: 1, title: 'Open the Spee\'V\'Dee App', desc: 'Log in to your account on iOS or Android.' },
                { step: 2, title: 'Go to Your Profile', desc: 'Tap your profile icon in the bottom navigation bar.' },
                { step: 3, title: 'Scroll to "Delete Account"', desc: 'You\'ll find the Delete Account button below the Log Out option.' },
                { step: 4, title: 'Confirm Deletion', desc: 'A confirmation dialog will appear. Tap "Yes, Delete My Account" to proceed.' },
                { step: 5, title: 'Account Deleted', desc: 'Your account and personal data will be permanently removed within 30 days.' },
              ].map(s => (
                <div key={s.step} style={{ display: 'flex', gap: 16 }}>
                  <div style={{ width: 36, height: 36, borderRadius: '50%', background: 'linear-gradient(135deg, #7851A9, #5B2D8E)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 14, flexShrink: 0 }}>{s.step}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#1F2937' }}>{s.title}</div>
                    <div style={{ fontSize: 13, color: '#6B7280', marginTop: 2 }}>{s.desc}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* What Gets Deleted vs Retained */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
            {/* Deleted */}
            <div style={{ background: '#fff', borderRadius: 16, padding: '24px 22px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', borderTop: '4px solid #DC2626' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <XCircle size={18} color="#DC2626" />
                <h4 style={{ margin: 0, fontSize: 15, color: '#DC2626' }}>Permanently Deleted</h4>
              </div>
              <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13, lineHeight: 2, color: '#374151' }}>
                <li>Your profile and personal information</li>
                <li>Login credentials and sessions</li>
                <li>Chat messages and conversations</li>
                <li>Push notification preferences</li>
                <li>KYC / ID verification data</li>
                <li>Real-time tracking history</li>
                <li>Support tickets</li>
                <li>Subscription records</li>
              </ul>
            </div>

            {/* Retained */}
            <div style={{ background: '#fff', borderRadius: 16, padding: '24px 22px', boxShadow: '0 2px 12px rgba(0,0,0,0.04)', borderTop: '4px solid #059669' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <CheckCircle size={18} color="#059669" />
                <h4 style={{ margin: 0, fontSize: 15, color: '#059669' }}>Retained (Legal Requirement)</h4>
              </div>
              <ul style={{ paddingLeft: 18, margin: 0, fontSize: 13, lineHeight: 2, color: '#374151' }}>
                <li>Anonymized historical order records</li>
                <li>Transaction records for tax/1099 compliance</li>
                <li>Financial records (retained for 7 years per IRS/tax law)</li>
              </ul>
              <p style={{ fontSize: 11, color: '#6B7280', marginTop: 12, lineHeight: 1.5 }}>
                Retained records are anonymized — all personally identifiable information (name, email, phone, address) is stripped. Only transaction amounts and dates are kept for legal compliance.
              </p>
            </div>
          </div>

          {/* Contact Support */}
          <div style={{ background: 'linear-gradient(135deg, #7851A9, #5B2D8E)', borderRadius: 16, padding: '32px', textAlign: 'center', color: '#fff' }}>
            <Trash2 size={28} color="rgba(255,255,255,0.8)" style={{ marginBottom: 12 }} />
            <h3 style={{ color: '#fff', margin: '0 0 8px', fontSize: 18 }}>Need Help?</h3>
            <p style={{ color: 'rgba(255,255,255,.75)', fontSize: 14, marginBottom: 20, lineHeight: 1.6 }}>
              If you're unable to delete your account through the app, or if you have questions about your data, contact our support team.
            </p>
            <a
              href="mailto:support@speedyvariety.com?subject=Account%20Deletion%20Request"
              style={{
                display: 'inline-block', padding: '14px 32px',
                background: '#fff', color: '#7851A9', borderRadius: 50,
                fontWeight: 700, fontSize: 14, textDecoration: 'none',
              }}
            >
              Contact Support
            </a>
            <p style={{ fontSize: 11, color: 'rgba(255,255,255,.5)', marginTop: 14 }}>support@speedyvariety.com</p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DeleteAccount;
